/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { apiClient } from '../../services/api';
import { Participant } from '../../types';
import './ReassignBidModal.css';

interface BulkReassignModalProps {
  bidIds: number[];
  onClose: () => void;
  onSuccess: () => void;
}

const BulkReassignModal: React.FC<BulkReassignModalProps> = ({ bidIds, onClose, onSuccess }) => {
  const [eligibleUsers, setEligibleUsers] = useState<Participant[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ successCount: number; failedCount: number; failedBids: any[] } | null>(null);

  useEffect(() => {
    loadEligibleUsers();
  }, []);

  const loadEligibleUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.getAllParticipants();

      if (!response.success || !response.participants) {
        setError('Failed to load users');
        return;
      }

      // Filter to only USER type participants who are not inactive
      const users = response.participants.filter(
        p => p.type === 'USER' && !p.userInactive
      );

      setEligibleUsers(users);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setResult(null);

      const response: any = await apiClient.bulkReassignBids(bidIds, Number(selectedUserId));

      if (response.success) {
        setResult({
          successCount: response.successCount || 0,
          failedCount: response.failedCount || 0,
          failedBids: response.failedBids || []
        });

        // If all succeeded, close after a delay
        if (response.failedCount === 0) {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } else {
        setError(response.error || 'Failed to reassign bids');
      }
    } catch (err: any) {
      console.error('Error bulk reassigning bids:', err);
      setError(err.response?.data?.error || err.message || 'Failed to reassign bids');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Bulk Reassign Bids</h2>
          <button className="close-button" onClick={onClose} disabled={isSaving}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {result && (
            <div className={result.failedCount === 0 ? "success-message" : "warning-message"}>
              <p><strong>Successfully reassigned:</strong> {result.successCount} bid(s)</p>
              {result.failedCount > 0 && (
                <>
                  <p><strong>Failed to reassign:</strong> {result.failedCount} bid(s)</p>
                  <ul className="failed-bids-list">
                    {result.failedBids.map((failed: any, idx: number) => (
                      <li key={idx}>
                        Bid ID {failed.bidId}: {failed.error}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          <div className="bid-info">
            <p><strong>Selected Bids:</strong> {bidIds.length}</p>
            <p className="info-text">All selected bids will be reassigned to the new author.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="newAuthor">New Author *</label>
              {isLoading ? (
                <p>Loading users...</p>
              ) : eligibleUsers.length === 0 ? (
                <p className="no-users-message">No eligible users available for reassignment</p>
              ) : (
                <select
                  id="newAuthor"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : '')}
                  required
                  disabled={isSaving || result !== null}
                >
                  <option value="">Select a user</option>
                  {eligibleUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.username})
                      {user.companyName && ` - ${user.companyName}`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={result && result.failedCount === 0 ? onSuccess : onClose}
                disabled={isSaving}
                className="cancel-button"
              >
                {result && result.failedCount === 0 ? 'Done' : 'Cancel'}
              </button>
              {(!result || result.failedCount > 0) && (
                <button
                  type="submit"
                  disabled={isSaving || !selectedUserId || isLoading}
                  className="submit-button"
                >
                  {isSaving ? 'Reassigning...' : `Reassign ${bidIds.length} Bid${bidIds.length > 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkReassignModal;
